/**
 * Context Switching Hooks
 *
 * React Query hooks for organization/network context switching.
 * Manages fetching available contexts and switching between them.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@/api/mutation';
import { useNavigate } from 'react-router-dom';
import {
  getOrganizations,
  getNetworks,
  switchContext,
  type AuthMeResponse,
  type ContextSwitchResponse,
} from '@/api/auth';
import type {
  OrganizationListItem,
  NetworkListItem,
  ContextSwitchInput,
} from '@/types';
import { PAGE_OWNS_FAILURE_META, REPORT_KNOWN_REFUSAL_META } from '@/api/queryClient';
import { REFERENCE_DATA_STALE_MS } from '@/constants/tuning';
import { getDefaultRoute } from '@/lib/defaultRoute';

// ============================================
// Query Keys
// ============================================

export const contextKeys = {
  all: ['context'] as const,
  organizations: () => [...contextKeys.all, 'organizations'] as const,
  networks: () => [...contextKeys.all, 'networks'] as const,
  user: ['auth', 'me'] as const,
};

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch organizations the user can switch to
 *
 * Returns orgs where user has admin/manager membership.
 * Supports server-side search by name/identifier.
 */
export function useOrganizations(options?: { enabled?: boolean; search?: string }) {
  const search = options?.search?.trim() || '';
  return useQuery<OrganizationListItem[], Error>({
    queryKey: [...contextKeys.organizations(), search],
    queryFn: () => getOrganizations(search || undefined),
    enabled: options?.enabled ?? true,
    staleTime: search ? 0 : REFERENCE_DATA_STALE_MS, // No caching when searching
  });
}

/**
 * Hook to fetch networks the user can switch to
 *
 * Returns networks where user has membership.
 * Supports server-side search by name/identifier.
 */
export function useNetworks(options?: { enabled?: boolean; search?: string }) {
  const search = options?.search?.trim() || '';
  return useQuery<NetworkListItem[], Error>({
    queryKey: [...contextKeys.networks(), search],
    queryFn: () => getNetworks(search || undefined),
    enabled: options?.enabled ?? true,
    staleTime: search ? 0 : REFERENCE_DATA_STALE_MS, // No caching when searching
  });
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to switch the user's active context.
 *
 * On success:
 * - Updates the user cache with the new context.
 * - Invalidates context lists (in case permissions changed).
 * - **Removes** every other query in the cache (bug-hunt #88). Previously
 *   only `contextKeys.all` and `contextKeys.user` were invalidated, so
 *   contacts/packets/checklists fetched in the prior org's context
 *   continued to render for a tick or remained available via cache. The
 *   safe default is to drop everything cross-context; queries that the
 *   new context cares about will re-fetch on next use.
 * - **Navigates to the new context's default home** (issue #4094). The
 *   user's prior route may not exist/apply in the new context, so we route
 *   them to where they belong (viewers → dashboard, else root). Centralized
 *   here so every consumer (switcher, forced-selection modal, staff create)
 *   behaves identically rather than each re-implementing the redirect.
 *
 * The hook always renders inside the router (all consumers are mounted under
 * the app's router tree), so `useNavigate` is safe here.
 */
export interface SwitchContextOptions {
  /**
   * Navigate to the new context's default home after a successful switch.
   * Default `true`. Pass `false` when the caller manages navigation itself
   * (e.g. the ctx-param gate, which strips `ctx` and stays on the current page).
   */
  navigateAfter?: boolean;
  /**
   * The caller's page presents a failed switch itself: the central router
   * reports it but pushes no global banner (Q-506 card 1571).
   */
  pageOwnsFailure?: boolean;
  /**
   * The caller shows a refused switch (`context_not_member` 403) as a failure, not
   * as its own refusal UX: report it, so its "Error reference" matches an alert
   * (#1685, Q-550). Off for the `?ctx=` gate, where that 403 is the expected outcome.
   */
  reportKnownRefusal?: boolean;
}

export function useSwitchContext(options?: SwitchContextOptions) {
  const navigateAfter = options?.navigateAfter ?? true;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<ContextSwitchResponse, Error, ContextSwitchInput>({
    feedbackKeyOf: ({ type, id }) => `${type}:${id}`,
    mutationFn: switchContext,
    meta:
      options?.pageOwnsFailure || options?.reportKnownRefusal
        ? {
            ...(options.pageOwnsFailure ? { [PAGE_OWNS_FAILURE_META]: true } : {}),
            ...(options.reportKnownRefusal ? { [REPORT_KNOWN_REFUSAL_META]: true } : {}),
          }
        : undefined,
    onSuccess: (response) => {
      // Bug-hunt #88: nuke every query that isn't tied to the auth/user
      // identity itself. Keep `auth/me` because we're about to update it.
      queryClient.removeQueries({
        predicate: (q) => {
          const key = q.queryKey;
          if (!Array.isArray(key) || key.length === 0) return true;
          // Preserve the auth/me query — we're about to write into it.
          if (key[0] === 'auth' && key[1] === 'me') return false;
          return true;
        },
      });

      // The switch endpoint returns ONLY `{ context }` — NOT a full profile.
      // Merge just the context onto the existing cached user so identity fields
      // (avatarUrl, isStaff, isSuperuser, …) survive; overwriting the whole
      // user with the partial response collapses the header avatar and user
      // menu until a manual reload refetches /me (XORG-005). Write AFTER
      // removeQueries so this fresh value isn't swept up.
      const merged = queryClient.setQueryData<AuthMeResponse>(
        contextKeys.user,
        (prev) => (prev ? { ...prev, context: response.context } : prev),
      );

      // Refetch the authoritative profile — context-scoped fields (org level,
      // access) can change with the new context — and repopulate the lists.
      void queryClient.invalidateQueries({ queryKey: contextKeys.user });
      void queryClient.invalidateQueries({ queryKey: contextKeys.all });

      if (navigateAfter) {
        // Route to the new context's default home (issue #4094) — always, on
        // every successful switch. Prefer the merged user; if the auth/me cache
        // wasn't populated yet, the new context alone resolves the effective
        // level for routing.
        const routingUser = merged ?? ({ context: response.context } as AuthMeResponse);
        void navigate(getDefaultRoute(routingUser));
      }
    },
  });
}

